import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search, Cloud, CloudOff, X } from 'lucide-react';

/**
 * Eine Domain aus unserem Bestand wählen.
 *
 * ══ WARUM NICHT WEITER EIN datalist ════════════════════════════════════════
 *
 * Hier stand ein `<input list="…">` mit einer `<datalist>`. Technisch ist das
 * eine Auswahl, optisch ist es ein leeres Textfeld — und es verhält sich wie
 * die Vorschlagsliste des Browsers, die früher eingetippte Werte anbietet.
 * Genau so wurde es gelesen: als Verlaufsliste, nicht als Bestand. Wer nicht
 * weiß, dass darin vierhundert gepflegte Domains liegen, tippt seine eigene
 * hinein.
 *
 * ══ WARUM KEIN GEWÖHNLICHES select ═════════════════════════════════════════
 *
 * Der alte Kommentar hatte damit recht: bei über vierhundert Einträgen ist
 * Tippen schneller als Scrollen, und ein `<select>` mit vierhundert `<option>`
 * ist unbedienbar. Deshalb beides — es sieht aus wie ein Auswahlfeld, und wer
 * tippt, filtert.
 *
 * ══ CLOUDFLARE IST DER UNTERSCHIED, DER ZÄHLT ══════════════════════════════
 *
 * Liegt eine Domain bei Cloudflare, setzt der DDNS-Beiwagen des Stacks den
 * A-Record selbst und zieht ihn beim Serverwechsel nach. Sonst muss das jemand
 * von Hand tun — und daran denken, wenn die Instanz umzieht. Das ist keine
 * Nebensache, deshalb steht es an jedem Eintrag und nicht nur in einer Fußnote.
 */

export interface DomainEintrag {
  id: string;
  name: string;
  cloudflareDetected?: boolean;
  cloudflareTunnel?: boolean;
}

export default function DomainAuswahl({
  domains,
  wert,
  onChange,
  nurCloudflare,
  onNurCloudflareChange,
  gesamtAnzahl,
  cloudflareAnzahl,
}: {
  domains: DomainEintrag[];
  wert: string;
  onChange: (name: string) => void;
  nurCloudflare: boolean;
  onNurCloudflareChange: (nur: boolean) => void;
  gesamtAnzahl: number;
  cloudflareAnzahl: number;
}) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [markiert, setMarkiert] = useState(0);
  const huelle = useRef<HTMLDivElement>(null);
  const sucheRef = useRef<HTMLInputElement>(null);

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();
    const liste = s ? domains.filter((d) => d.name.toLowerCase().includes(s)) : domains;
    // Die ohne Handarbeit zuerst — das ist fast immer die richtige Wahl.
    return [...liste].sort((a, b) => {
      const ca = a.cloudflareDetected || a.cloudflareTunnel ? 0 : 1;
      const cb = b.cloudflareDetected || b.cloudflareTunnel ? 0 : 1;
      return ca - cb || a.name.localeCompare(b.name);
    });
  }, [domains, suche]);

  // Schliessen, wenn daneben geklickt wird.
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [offen]);

  useEffect(() => {
    if (offen) {
      setMarkiert(0);
      // Sofort tippen können, ohne erst ins Suchfeld zu klicken.
      window.setTimeout(() => sucheRef.current?.focus(), 0);
    } else {
      setSuche('');
    }
  }, [offen]);

  const waehlen = (name: string) => {
    onChange(name);
    setOffen(false);
  };

  const beiTaste = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarkiert((m) => Math.min(m + 1, gefiltert.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarkiert((m) => Math.max(m - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const d = gefiltert[markiert];
      if (d) waehlen(d.name);
    } else if (e.key === 'Escape') {
      setOffen(false);
    }
  };

  const gewaehlt = domains.find((d) => d.name === wert);
  const gewaehltBeiCloudflare = !!(gewaehlt?.cloudflareDetected || gewaehlt?.cloudflareTunnel);

  return (
    <div ref={huelle} className="relative">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 ${
          wert ? 'border-slate-300 bg-white' : 'border-slate-300 bg-white text-slate-400'
        }`}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {wert && (
            <span title={gewaehltBeiCloudflare ? 'bei Cloudflare' : 'nicht bei Cloudflare'}>
              {gewaehltBeiCloudflare ? (
                <Cloud size={12} className="shrink-0 text-emerald-600" />
              ) : (
                <CloudOff size={12} className="shrink-0 text-amber-600" />
              )}
            </span>
          )}
          <span className={`truncate ${wert ? 'font-mono text-slate-800' : ''}`}>
            {wert || 'Domain wählen …'}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {wert && (
            <span
              role="button"
              tabIndex={-1}
              title="Auswahl aufheben"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={11} />
            </span>
          )}
          <ChevronDown size={13} className="text-slate-400" />
        </span>
      </button>

      {offen && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-surface shadow-lg">
          <div className="flex items-center gap-1.5 border-b border-slate-100 px-2 py-1.5">
            <Search size={12} className="shrink-0 text-slate-400" />
            <input
              ref={sucheRef}
              value={suche}
              onChange={(e) => {
                setSuche(e.target.value);
                setMarkiert(0);
              }}
              onKeyDown={beiTaste}
              placeholder={`${domains.length} Domains durchsuchen …`}
              className="w-full bg-transparent text-xs outline-none"
            />
          </div>

          <div className="max-h-56 overflow-y-auto">
            {gefiltert.length === 0 && (
              <p className="px-3 py-3 text-xs text-slate-400">
                Keine Domain passt zu „{suche}".
              </p>
            )}
            {gefiltert.map((d, i) => {
              const beiCf = !!(d.cloudflareDetected || d.cloudflareTunnel);
              return (
                <button
                  key={d.id}
                  type="button"
                  onMouseEnter={() => setMarkiert(i)}
                  onClick={() => waehlen(d.name)}
                  className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs ${
                    i === markiert ? 'bg-brand-50' : ''
                  }`}
                >
                  {beiCf ? (
                    <Cloud size={12} className="shrink-0 text-emerald-600" />
                  ) : (
                    <CloudOff size={12} className="shrink-0 text-amber-600" />
                  )}
                  <span className="flex-1 truncate font-mono text-slate-700">{d.name}</span>
                  {!beiCf && (
                    <span className="shrink-0 text-[10px] text-amber-700">A-Record von Hand</span>
                  )}
                  {d.name === wert && <Check size={12} className="shrink-0 text-brand-600" />}
                </button>
              );
            })}
          </div>

          <div className="border-t border-slate-100 px-2.5 py-1.5">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <input
                type="checkbox"
                checked={nurCloudflare}
                onChange={(e) => onNurCloudflareChange(e.target.checked)}
              />
              nur die {cloudflareAnzahl} bei Cloudflare
              <span className="text-slate-400">(von {gesamtAnzahl})</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
